import * as React from 'react';
import {Link as RouterLink, NavLink, useLocation} from "react-router-dom";
import {alpha, useTheme} from "@mui/material/styles";
import {
    AppBar as MuiAppBar,
    Box,
    Container,
    IconButton,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography
} from "@mui/material";

// Icon
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import GitHubIcon from '@mui/icons-material/GitHub';
import MenuIcon from '@mui/icons-material/Menu';

// Theme, components
import {ColorModeContext} from "../theme/defaultTheme";
import BrandMark from "./BrandMark";

const NAV_ITEMS = [
    {label: '도구', to: '/'},
    {label: '업데이트', to: '/update'},
];

function ResponsiveAppBar(): JSX.Element {
    const colorMode = React.useContext(ColorModeContext);
    const theme = useTheme();
    const location = useLocation();
    const isDarkMode = theme.palette.mode === 'dark';
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

    // 경로가 바뀌면 모바일 메뉴는 닫아 둔다.
    React.useEffect(() => {
        setMenuAnchor(null);
    }, [location.pathname]);

    return (
        <MuiAppBar
            position="sticky"
            elevation={0}
            sx={{
                bgcolor: alpha(theme.palette.background.default, 0.82),
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backdropFilter: 'saturate(180%) blur(10px)',
            }}
        >
            <Container maxWidth="md">
                <Toolbar disableGutters variant="dense" sx={{minHeight: 56, gap: 1}}>
                    {/* 브랜드 로고 */}
                    <Box
                        component={RouterLink}
                        to="/"
                        aria-label="MakerKSH Utils 홈"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            textDecoration: 'none',
                            marginRight: 'auto',
                        }}
                    >
                        <BrandMark height={24}/>
                        <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.75}}>
                            <Typography component="span" variant="subtitle1" sx={{fontWeight: 800, color: 'text.primary'}}>
                                MakerKSH
                            </Typography>
                            <Typography component="span" variant="subtitle2" sx={{fontWeight: 600, color: 'text.secondary'}}>
                                Utils
                            </Typography>
                        </Box>
                    </Box>

                    {/* 데스크톱 내비게이션 */}
                    <Box sx={{display: {xs: 'none', sm: 'flex'}, alignItems: 'center', gap: 2.5, marginRight: 1}}>
                        {NAV_ITEMS.map((item) => (
                            <Typography
                                key={item.to}
                                component={NavLink}
                                to={item.to}
                                end={item.to === '/'}
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    color: 'text.secondary',
                                    paddingY: 0.5,
                                    borderBottom: '2px solid transparent',
                                    transition: 'color 0.15s ease, border-color 0.15s ease',
                                    ':hover': {color: 'text.primary'},
                                    '&.active': {
                                        color: 'text.primary',
                                        borderBottomColor: 'primary.main',
                                    },
                                }}
                            >
                                {item.label}
                            </Typography>
                        ))}
                    </Box>

                    {/* 테마 전환 · GitHub */}
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <Tooltip title={isDarkMode ? '라이트 모드' : '다크 모드'}>
                            <IconButton
                                size="small"
                                aria-label="테마 전환"
                                onClick={colorMode.toggleMode}
                                sx={{color: 'text.secondary'}}
                            >
                                {isDarkMode ? <LightModeOutlinedIcon fontSize="small"/> :
                                    <DarkModeOutlinedIcon fontSize="small"/>}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="GitHub">
                            <IconButton
                                size="small"
                                aria-label="GitHub"
                                component="a"
                                href="https://github.com/SeHyun-Kim04"
                                target="_blank"
                                rel="noopener"
                                sx={{color: 'text.secondary'}}
                            >
                                <GitHubIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            aria-label="메뉴 열기"
                            onClick={(event) => setMenuAnchor(event.currentTarget)}
                            sx={{display: {xs: 'inline-flex', sm: 'none'}, color: 'text.secondary'}}
                        >
                            <MenuIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    {/* 모바일 내비게이션 */}
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                        transformOrigin={{vertical: 'top', horizontal: 'right'}}
                        slotProps={{paper: {sx: {minWidth: 180, border: '1px solid', borderColor: 'divider'}}}}
                    >
                        {NAV_ITEMS.map((item) => (
                            <MenuItem
                                key={item.to}
                                component={RouterLink}
                                to={item.to}
                                onClick={() => setMenuAnchor(null)}
                            >
                                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                    {item.label}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </Toolbar>
            </Container>
        </MuiAppBar>
    );
}

export default ResponsiveAppBar;
